import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { DataTable, DataTableFilterEvent, DataTablePageEvent, DataTableSelectionSingleChangeEvent, DataTableExpandedRows } from 'primereact/datatable';
import { Column, ColumnEditorOptions, ColumnEvent } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { FilterMatchMode } from 'primereact/api';
import { Menubar } from 'primereact/menubar';
import { Tag } from 'primereact/tag';
import { accountsApi, checksApi, dataSourcesApi } from '../../api';
import { formatCurrency, formatDate } from '../../utils';
import { Account, Check, DataSource } from '../../types';
import { BoundingBox } from './PdfViewer';
import { useDataTableState, useAccountMenuItem } from '../../hooks';

interface EditedCheck extends Check {
  edited?: boolean;
}

interface ChecksTabProps {
  clientId: string;
  onRowSelect?: (
    check: Check | null,
    dataSource: DataSource | null,
    pageNumber?: number,
    boundingBox?: BoundingBox | null
  ) => void;
}

export const ChecksTab: React.FC<ChecksTabProps> = ({ clientId, onRowSelect }) => {
  const toast = useRef<Toast>(null);
  const tableRef = useRef<DataTable<any>>(null);
  const [checks, setChecks] = useState<EditedCheck[]>([]);
  const [originalChecks, setOriginalChecks] = useState<EditedCheck[]>([]);
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filteredRowCount, setFilteredRowCount] = useState<number>(0);
  const [selectedCheck, setSelectedCheck] = useState<EditedCheck | null>(null);
  const [isApprovingAll, setIsApprovingAll] = useState(false);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(25);
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>({});

  // Persistent filter and sort state
  const {
    sortField,
    sortOrder,
    handleSort,
    filters,
    handleFilter: onFilterChange,
    customFilters,
    setCustomFilter,
  } = useDataTableState({
    storageKey: 'dataReview:checks',
    defaultSort: { field: 'check_date', order: -1 },
    defaultFilters: {
      check_number: { value: null, matchMode: FilterMatchMode.CONTAINS },
      payee_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
      memo: { value: null, matchMode: FilterMatchMode.CONTAINS },
    },
    defaultCustomFilters: {
      accountFilter: 'all',
      fileFilter: 'all',
      includePending: true,
      includeApproved: false,
      includeRejected: false,
    },
  });

  // Extract custom filters for easier access
  const accountFilter = customFilters.accountFilter as string;
  const fileFilter = customFilters.fileFilter as string;
  const includePending = customFilters.includePending as boolean;
  const includeApproved = customFilters.includeApproved as boolean;
  const includeRejected = customFilters.includeRejected as boolean;

  // Duplicate review mode is temporary (not persisted)
  const [includeDuplicates, setIncludeDuplicates] = useState(false);

  useEffect(() => {
    loadChecks();
  }, [clientId]);

  const loadChecks = async () => {
    try {
      setIsLoading(true);
      const [checksData, sourcesData, accountsData] = await Promise.all([
        checksApi.listChecks(clientId),
        dataSourcesApi.listDataSources(clientId),
        accountsApi.listAccounts(clientId),
      ]);
      const editedChecksData = checksData.map(c => ({ ...c, edited: false }));
      setChecks(editedChecksData);
      setOriginalChecks(editedChecksData.map(c => ({ ...c })));
      setDataSources(sourcesData);
      setAccounts(accountsData);
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to load checks',
        life: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getCheckStatus = (c: EditedCheck): string => {
    return c.status || 'pending';
  };

  const isStatusApproved = (status: string) => status === 'approved';
  const isStatusRejected = (status: string) => status === 'rejected';
  const isStatusPending = (status: string) => !status || status === 'pending';

  // Get data sources that have checks (bank statements)
  const checkDataSources = useMemo(() => {
    return dataSources.filter((ds) => ds.source_type === 'bank_statement');
  }, [dataSources]);

  // Account filter options - only accounts that have check files
  const filteredAccounts = useMemo(() => {
    const accountIds = new Set(
      checkDataSources
        .map((ds) => ds.account_id)
        .filter((id): id is string => id !== null)
    );
    return accounts.filter((a) => accountIds.has(a.id));
  }, [accounts, checkDataSources]);

  const { accountMenuItem } = useAccountMenuItem(clientId, {
    showAllOption: true,
    customAccounts: filteredAccounts,
    customSelectedId: accountFilter === 'all' ? null : accountFilter,
    customSetAccount: (id) => setCustomFilter('accountFilter', id ?? 'all'),
  });

  // File filter options - filtered by selected account
  const fileFilterOptions = useMemo(() => {
    let relevantSources = checkDataSources;

    // Filter by account if one is selected
    if (accountFilter !== 'all') {
      relevantSources = relevantSources.filter((ds) => ds.account_id === accountFilter);
    }

    return [
      { label: 'All files', value: 'all' },
      ...relevantSources.map((ds) => ({
        label: ds.original_filename || ds.name || `Source ${ds.id}`,
        value: ds.id,
      })),
    ];
  }, [checkDataSources, accountFilter]);

  // Count duplicates in the dataset
  const totalDuplicateCount = useMemo(() => {
    return checks.filter((c) => c.is_potential_duplicate).length;
  }, [checks]);

  // Group duplicates by their original check for the review mode
  const duplicateGroups = useMemo(() => {
    if (!includeDuplicates) return { originals: [] as EditedCheck[], duplicatesByOriginalId: new Map<string, EditedCheck[]>() };

    // Find all duplicates and group by their original
    const duplicates = checks.filter((c) => c.is_potential_duplicate && c.duplicate_of_id);
    const duplicatesByOriginalId = new Map<string, EditedCheck[]>();

    for (const dup of duplicates) {
      const originalId = dup.duplicate_of_id!;
      if (!duplicatesByOriginalId.has(originalId)) {
        duplicatesByOriginalId.set(originalId, []);
      }
      duplicatesByOriginalId.get(originalId)!.push(dup);
    }

    // Get the original checks that have duplicates
    const originalIds = new Set(duplicatesByOriginalId.keys());
    const originals = checks.filter((c) => originalIds.has(c.id));

    return { originals, duplicatesByOriginalId };
  }, [checks, includeDuplicates]);

  const filteredChecks = useMemo(() => {
    // In duplicate review mode, show originals that have duplicates
    if (includeDuplicates) {
      return duplicateGroups.originals;
    }

    let result = checks;

    // Filter by account (via data sources)
    if (accountFilter !== 'all') {
      const accountSourceIds = new Set(
        checkDataSources
          .filter((ds) => ds.account_id === accountFilter)
          .map((ds) => ds.id)
      );
      result = result.filter((c) => accountSourceIds.has(c.data_source_id));
    }

    // Filter by file
    if (fileFilter !== 'all') {
      result = result.filter((c) => c.data_source_id === fileFilter);
    }

    // Filter by status
    result = result.filter((c) => {
      const status = getCheckStatus(c);
      if (isStatusPending(status) && !includePending) return false;
      if (isStatusApproved(status) && !includeApproved) return false;
      if (isStatusRejected(status) && !includeRejected) return false;
      return true;
    });

    // Exclude duplicates in normal mode
    result = result.filter((c) => !c.is_potential_duplicate);

    return result;
  }, [checks, accountFilter, fileFilter, checkDataSources, includePending, includeApproved, includeRejected, includeDuplicates, duplicateGroups]);

  // Get deletable duplicates (based on file/account filter, regardless of includeDuplicates toggle)
  const deletableDuplicates = useMemo(() => {
    let result = checks;

    // Filter by account (via data sources)
    if (accountFilter !== 'all') {
      const accountSourceIds = new Set(
        checkDataSources
          .filter((ds) => ds.account_id === accountFilter)
          .map((ds) => ds.id)
      );
      result = result.filter((c) => accountSourceIds.has(c.data_source_id));
    }

    // Filter by file
    if (fileFilter !== 'all') {
      result = result.filter((c) => c.data_source_id === fileFilter);
    }

    return result.filter((c) => c.is_potential_duplicate);
  }, [checks, accountFilter, fileFilter, checkDataSources]);

  // Calculate total amount (excluding duplicates)
  const totalAmount = useMemo(() => {
    return filteredChecks
      .filter((c) => !c.is_potential_duplicate)
      .reduce((sum, c) => sum + (c.amount || 0), 0);
  }, [filteredChecks]);

  useEffect(() => {
    setFilteredRowCount(filteredChecks.length);
  }, [filteredChecks]);

  // Reset file filter when account filter changes
  useEffect(() => {
    setCustomFilter('fileFilter', 'all');
  }, [accountFilter]);

  const handleFilter = (event: DataTableFilterEvent) => {
    onFilterChange(event);
    const filtered = event.filteredValue as Check[] | null;
    setFilteredRowCount(filtered ? filtered.length : filteredChecks.length);
  };

  const onPage = (e: DataTablePageEvent) => {
    setFirst(e.first);
    setRows(e.rows);
    setTimeout(() => {
      const wrapper = tableRef.current?.getElement()?.querySelector('.p-datatable-wrapper');
      if (wrapper) {
        wrapper.scrollTop = 0;
      }
    }, 0);
  };

  const handleSelectionChange = (e: DataTableSelectionSingleChangeEvent<EditedCheck[]>) => {
    const selected = e.value as EditedCheck | null;
    setSelectedCheck(selected);
    if (onRowSelect) {
      const dataSource = selected
        ? dataSources.find((ds) => ds.id === selected.data_source_id) || null
        : null;
      const pageNumber = selected?.page_number ?? undefined;
      const boundingBox = selected?.bbox_json ?? null;
      onRowSelect(selected, dataSource, pageNumber, boundingBox);
    }
  };

  const handleApprove = async (check: EditedCheck) => {
    try {
      const updated = await checksApi.approveCheck(clientId, check.id);
      setChecks(checks.map((c) => (c.id === check.id ? { ...updated, edited: false } : c)));
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Check approved',
        life: 3000,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to approve check',
        life: 5000,
      });
    }
  };

  const handleReject = async (check: EditedCheck) => {
    confirmDialog({
      message: 'Are you sure you want to reject this check?',
      header: 'Confirm Check Rejection',
      icon: 'ms-out ms-out-warning icon-warning',
      acceptClassName: 'p-button-warning',
      accept: async () => {
        try {
          const updated = await checksApi.rejectCheck(clientId, check.id);
          setChecks(checks.map((c) => (c.id === check.id ? { ...updated, edited: false } : c)));
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Check rejected',
            life: 3000,
          });
        } catch (error: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.detail || 'Failed to reject check',
            life: 5000,
          });
        }
      },
    });
  };

  const handleUndo = (checkId: string) => {
    const original = originalChecks.find((c) => c.id === checkId);
    if (!original) return;

    setChecks((prev) => prev.map((c) => (c.id === checkId ? { ...original, edited: false } : c)));
    setSelectedCheck((prev) => (prev && prev.id === checkId ? { ...original, edited: false } : prev));
    toast.current?.show({
      severity: 'info',
      summary: 'Undone',
      detail: 'Changes reverted',
      life: 2000,
    });
  };

  const handleUnlock = async (check: EditedCheck) => {
    confirmDialog({
      message: 'This check is approved. Unlock to allow edits?',
      header: 'Confirm Unlock Check',
      icon: 'ms-out ms-out-warning icon-warning',
      acceptClassName: 'p-button-warning',
      acceptLabel: 'Unlock',
      rejectLabel: 'Cancel',
      accept: async () => {
        try {
          const updated = await checksApi.unlockCheck(clientId, check.id);
          setChecks(checks.map((c) => (c.id === check.id ? { ...updated, edited: false } : c)));
          toast.current?.show({
            severity: 'success',
            summary: 'Unlocked',
            detail: 'Check unlocked for editing',
            life: 3000,
          });
        } catch (error: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.detail || 'Failed to unlock check',
            life: 5000,
          });
        }
      },
    });
  };

  const handleApproveAll = async () => {
    const pendingChecks = filteredChecks.filter((c) => isStatusPending(getCheckStatus(c)));
    if (pendingChecks.length === 0) {
      toast.current?.show({
        severity: 'info',
        summary: 'No Pending',
        detail: 'No pending checks to approve',
        life: 3000,
      });
      return;
    }

    confirmDialog({
      message: `Are you sure you want to approve all ${pendingChecks.length} pending checks?`,
      header: 'Approve All Checks',
      icon: 'ms-out ms-out-check-circle icon-success',
      acceptClassName: 'p-button-success',
      accept: async () => {
        setIsApprovingAll(true);
        try {
          for (const check of pendingChecks) {
            await checksApi.approveCheck(clientId, check.id);
          }
          await loadChecks();
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: `${pendingChecks.length} checks approved`,
            life: 3000,
          });
        } catch (error: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.detail || 'Failed to approve all checks',
            life: 5000,
          });
        } finally {
          setIsApprovingAll(false);
        }
      },
    });
  };

  const handleShowDuplicates = useCallback(() => {
    // Set all filters to show duplicates
    setCustomFilter('accountFilter', 'all');
    setCustomFilter('fileFilter', 'all');
    setCustomFilter('includePending', true);
    setIncludeDuplicates(true);

    // Expand all rows by default - need to wait for state to update
    setTimeout(() => {
      const allOriginalIds = checks
        .filter((c) => c.is_potential_duplicate && c.duplicate_of_id)
        .map((c) => c.duplicate_of_id!)
        .filter((id, index, arr) => arr.indexOf(id) === index);

      const expanded: DataTableExpandedRows = {};
      allOriginalIds.forEach((id) => {
        expanded[id] = true;
      });
      setExpandedRows(expanded);
    }, 0);
  }, [checks, setCustomFilter]);

  const handleDeleteVisibleDuplicates = () => {
    if (deletableDuplicates.length === 0) return;

    confirmDialog({
      message: `Are you sure you want to delete ${deletableDuplicates.length} duplicate check${deletableDuplicates.length === 1 ? '' : 's'}? This action cannot be undone.`,
      header: 'Delete Duplicates',
      icon: 'ms-out ms-out-warning icon-warning',
      acceptClassName: 'p-button-danger',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      accept: async () => {
        try {
          const checkIds = deletableDuplicates.map((c) => c.id);
          const result = await checksApi.bulkDeleteChecks(clientId, checkIds);
          await loadChecks();
          toast.current?.show({
            severity: 'success',
            summary: 'Deleted',
            detail: `${result.deleted_count} duplicate checks deleted`,
            life: 3000,
          });
        } catch (error: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.detail || 'Failed to delete duplicates',
            life: 5000,
          });
        }
      },
    });
  };

  const isRowLocked = (rowData: EditedCheck) => {
    const status = getCheckStatus(rowData);
    return isStatusApproved(status);
  };

  const cellEditor = (options: ColumnEditorOptions) => {
    if (isRowLocked(options.rowData as EditedCheck)) {
      return <span>{options.value || ''}</span>;
    }

    const rowData = options.rowData as Check;
    // Use display value (final || raw) as the initial editor value
    const displayValue = getDisplayValue(rowData, options.field);

    if (options.field === 'amount') {
      const numValue = typeof displayValue === 'string' ? parseFloat(displayValue) : displayValue;
      return (
        <InputNumber
          value={isNaN(numValue) ? null : numValue}
          onValueChange={(e) => options.editorCallback?.(e.value)}
          mode="currency"
          currency="USD"
          locale="en-US"
          minFractionDigits={2}
          className="full-width-input"
        />
      );
    }

    if (options.field === 'check_date') {
      let dateValue = displayValue || '';
      if (dateValue && !dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const parsed = new Date(dateValue);
        if (!isNaN(parsed.getTime())) {
          dateValue = parsed.toISOString().split('T')[0];
        }
      }
      return (
        <InputText
          type="date"
          value={dateValue}
          onChange={(e) => options.editorCallback?.(e.target.value)}
          className="full-width-input"
        />
      );
    }

    return (
      <InputText
        type="text"
        value={displayValue || ''}
        onChange={(e) => options.editorCallback?.(e.target.value)}
        onKeyDown={(e) => e.stopPropagation()}
        className="full-width-input"
        autoFocus
      />
    );
  };

  const onCellEditComplete = async (e: ColumnEvent) => {
    const { rowData, newValue, field } = e;
    const check = rowData as EditedCheck;

    if (isRowLocked(check)) return;

    // Compare with display value (final || raw) to see if actually changed
    const currentDisplayValue = getDisplayValue(check, field);
    if (newValue === currentDisplayValue) return;

    // Update locally first and mark as edited
    const updatedCheck: EditedCheck = {
      ...check,
      [field]: newValue,
      edited: true,
    };

    setChecks((prev) => prev.map((c) => (c.id === check.id ? updatedCheck : c)));
    setSelectedCheck((prev) => (prev && prev.id === check.id ? updatedCheck : prev));

    // Save to backend
    try {
      const updateData: Record<string, any> = { [field]: newValue };
      const saved = await checksApi.updateCheck(clientId, check.id, updateData);
      setChecks((prev) => prev.map((c) => (c.id === check.id ? { ...saved, edited: false } : c)));
      setSelectedCheck((prev) => (prev && prev.id === check.id ? { ...saved, edited: false } : prev));
    } catch (error: any) {
      // Revert on error
      setChecks((prev) => prev.map((c) => (c.id === check.id ? check : c)));
      setSelectedCheck((prev) => (prev && prev.id === check.id ? check : prev));
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to save changes',
        life: 5000,
      });
    }
  };

  // Helper to get display value: final field if set, otherwise raw field
  const getDisplayValue = (row: Check, field: string): any => {
    const finalValue = row[field as keyof Check];
    if (finalValue !== null && finalValue !== undefined && finalValue !== '') {
      return finalValue;
    }
    // Map final fields to raw fields
    const rawFieldMap: Record<string, keyof Check> = {
      check_number: 'raw_check_no',
      check_date: 'raw_date',
      amount: 'raw_amount',
      payee_name: 'raw_payee',
      memo: 'raw_memo',
    };
    const rawField = rawFieldMap[field];
    if (rawField) {
      return row[rawField];
    }
    return finalValue;
  };

  // Check if showing raw value (for styling purposes)
  const isShowingRawValue = (row: Check, field: string): boolean => {
    const finalValue = row[field as keyof Check];
    return finalValue === null || finalValue === undefined || finalValue === '';
  };

  const dateBodyTemplate = (rowData: Check) => {
    const value = getDisplayValue(rowData, 'check_date');
    if (!value) return '—';
    const isRaw = isShowingRawValue(rowData, 'check_date');
    const formatted = formatDate(value);
    return isRaw ? <span className="raw-value">{formatted}</span> : formatted;
  };

  const amountBodyTemplate = (rowData: Check) => {
    // Check final amount first
    if (rowData.amount !== null && rowData.amount !== undefined) {
      return formatCurrency(rowData.amount);
    }

    // Fall back to raw_amount
    if (rowData.raw_amount) {
      const numValue = parseFloat(rowData.raw_amount);
      if (!isNaN(numValue)) {
        return <span className="raw-value">{formatCurrency(numValue)}</span>;
      }
      return <span className="raw-value">{rowData.raw_amount}</span>;
    }

    return '—';
  };

  const payeeBodyTemplate = (rowData: Check) => {
    const value = getDisplayValue(rowData, 'payee_name');
    if (!value) return '—';
    const isRaw = isShowingRawValue(rowData, 'payee_name');
    return isRaw ? <span className="raw-value">{value}</span> : value;
  };

  const memoBodyTemplate = (rowData: Check) => {
    const value = getDisplayValue(rowData, 'memo');
    if (!value) return '—';
    const isRaw = isShowingRawValue(rowData, 'memo');
    return isRaw ? <span className="raw-value">{value}</span> : value;
  };

  const checkNumberBodyTemplate = (rowData: Check) => {
    const value = getDisplayValue(rowData, 'check_number');
    if (!value) return '—';
    const isRaw = isShowingRawValue(rowData, 'check_number');
    return isRaw ? <span className="raw-value">{value}</span> : value;
  };

  const actionBodyTemplate = (rowData: EditedCheck) => {
    const status = getCheckStatus(rowData);

    // Non-pending rows are locked
    if (!isStatusPending(status)) {
      return (
        <div className="row-actions">
          <Button
            icon="ms-out ms-out-lock"
            link
            tooltip="Unlock for edit"
            onClick={() => handleUnlock(rowData)}
          />
        </div>
      );
    }

    return (
      <div className="row-actions">
        <Button
          icon="ms-out ms-out-check"
          link
          tooltip="Approve"
          severity="success"
          onClick={() => handleApprove(rowData)}
        />
        <Button
          icon="ms-out ms-out-close"
          link
          tooltip="Reject"
          severity="danger"
          onClick={() => handleReject(rowData)}
        />
        {rowData.edited && (
          <Button
            icon="ms-out ms-out-undo"
            link
            tooltip="Undo changes"
            severity="info"
            onClick={() => handleUndo(rowData.id)}
          />
        )}
      </div>
    );
  };

  const statusBodyTemplate = (rowData: EditedCheck) => {
    const status = getCheckStatus(rowData);
    let statusIcon;
    if (isStatusApproved(status)) {
      statusIcon = <i className="ms-out ms-out-check-circle ms-success" />;
    } else if (isStatusRejected(status)) {
      statusIcon = <i className="ms-out ms-out-cancel ms-danger" />;
    } else {
      statusIcon = <i className="ms-out ms-out-error-circle ms-info" />;
    }

    return (
      <span>
        {statusIcon}
        {rowData.is_potential_duplicate && (
          <Tag value="dupe" severity="warning" />
        )}
      </span>
    );
  };

  // Row expansion template for showing duplicates of an original
  const rowExpansionTemplate = (originalCheck: EditedCheck) => {
    const duplicates = duplicateGroups.duplicatesByOriginalId.get(originalCheck.id) || [];

    return (
      <div>
        <DataTable
          value={duplicates}
          dataKey="id"
          size="small"
          className="no-headers warnlight"
        >
          <Column
            body={statusBodyTemplate}
            style={{ width: '6%' }}
          />
          <Column
            field="check_number"
            header="#"
            body={checkNumberBodyTemplate}
            style={{ width: '12%' }}
          />
          <Column
            field="check_date"
            header="Date"
            body={dateBodyTemplate}
            style={{ width: '12%' }}
          />
          <Column
            field="payee_name"
            header="Payee"
            body={payeeBodyTemplate}
            style={{ width: '25%' }}
          />
          <Column
            field="memo"
            header="Memo"
            body={memoBodyTemplate}
            style={{ width: '20%' }}
          />
          <Column
            field="amount"
            header="Amount"
            body={amountBodyTemplate}
            style={{ width: '12%' }}
          />
          <Column
            body={(rowData: EditedCheck) => (
              <Button
                icon="ms-out ms-out-check"
                link
                severity="success"
                tooltip="Approve"
                onClick={() => handleApprove(rowData)}
              />
            )}
          />
        </DataTable>
      </div>
    );
  };

  const currentFileLabel = useMemo(() => {
    if (fileFilter === 'all') return 'All files';
    const option = fileFilterOptions.find((o) => o.value === fileFilter);
    return option?.label || 'All files';
  }, [fileFilter, fileFilterOptions]);

  const menuItems = [
    accountMenuItem,
    {
      label: `${currentFileLabel}`,
      icon: 'ms-out ms-out-description',
      items: fileFilterOptions.map((opt) => ({
        label: opt.label,
        command: () => setCustomFilter('fileFilter', opt.value),
      })),
    },
    {
      label: 'Approve All',
      icon: 'ms-out ms-out-done-all',
      command: handleApproveAll,
      disabled: isApprovingAll || filteredChecks.length === 0,
    },
    {
      label: 'Status',
      icon: 'ms-out ms-out-visibility',
      items: [
        {
          label: includePending ? 'Hide Pending' : 'Show Pending',
          command: () => setCustomFilter('includePending', !includePending),
        },
        {
          label: includeApproved ? 'Hide Approved' : 'Show Approved',
          command: () => setCustomFilter('includeApproved', !includeApproved),
        },
        {
          label: includeRejected ? 'Hide Rejected' : 'Show Rejected',
          command: () => setCustomFilter('includeRejected', !includeRejected),
        },
      ],
    },
  ];

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />

      <Menubar
        className="actions-menubar"
        model={menuItems}
        end={
          totalDuplicateCount > 0 ? (
            <div className="menu-item-actions-group warning">
              <label><i className="ms-out ms-out-warning" /> dupes ({totalDuplicateCount}): </label>
              <Button
                icon="ms-out ms-out-preview"
                label="review"
                severity="warning"
                size="small"
                tooltip="review duplicates"
                tooltipOptions={{ position: 'bottom' }}
                onClick={handleShowDuplicates}
              />
              {deletableDuplicates.length > 0 && (
                <Button
                  label="delete"
                  severity="danger"
                  icon="ms-out ms-out-delete"
                  size="small"
                  tooltip="delete duplicates"
                  tooltipOptions={{ position: 'bottom' }}
                  onClick={handleDeleteVisibleDuplicates}
                />
              )}
            </div>
          ) : undefined
        }
      />

      <DataTable
        ref={tableRef}
        value={filteredChecks}
        dataKey="id"
        loading={isLoading}
        selectionMode="single"
        selection={selectedCheck}
        onSelectionChange={handleSelectionChange}
        paginator
        first={first}
        rows={rows}
        onPage={onPage}
        rowsPerPageOptions={[25, 50, 100]}
        paginatorTemplate={{
          layout: 'RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport',
          CurrentPageReport: (options) => (
            <span className="paginator-current">
              Showing {options.first}-{options.last} of <b>{options.totalRecords}</b> {includeDuplicates ? 'originals' : 'checks'}
              {totalAmount > 0 && <> · Total: <b>{formatCurrency(totalAmount)}</b></>}
            </span>
          ),
        }}
        size="small"
        stripedRows
        scrollable
        scrollHeight="flex"
        filterDisplay="menu"
        filters={filters}
        onFilter={handleFilter}
        totalRecords={filteredRowCount}
        emptyMessage={includeDuplicates ? 'No duplicates found' : 'No checks found'}
        sortField={sortField ?? undefined}
        sortOrder={sortOrder ?? undefined}
        onSort={handleSort}
        editMode="cell"
        tableStyle={{ tableLayout: 'fixed' }}
        expandedRows={includeDuplicates ? expandedRows : undefined}
        onRowToggle={(e) => setExpandedRows(e.data as DataTableExpandedRows)}
        rowExpansionTemplate={includeDuplicates ? rowExpansionTemplate : undefined}
      >
        {includeDuplicates && (
          <Column expander style={{ width: '3%' }} />
        )}
        <Column
          body={statusBodyTemplate}
          style={{ width: '5%' }}
        />
        <Column
          field="check_number"
          header="#"
          body={checkNumberBodyTemplate}
          sortable={!includeDuplicates}
          filter={!includeDuplicates}
          filterField="check_number"
          filterPlaceholder="Search"
          showFilterMatchModes={false}
          style={{ width: '12%' }}
          editor={!includeDuplicates ? cellEditor : undefined}
          onCellEditComplete={!includeDuplicates ? onCellEditComplete : undefined}
        />
        <Column
          field="check_date"
          header="Date"
          body={dateBodyTemplate}
          style={{ width: '12%' }}
          sortable={!includeDuplicates}
          editor={!includeDuplicates ? cellEditor : undefined}
          onCellEditComplete={!includeDuplicates ? onCellEditComplete : undefined}
        />
        <Column
          field="payee_name"
          header="Payee"
          body={payeeBodyTemplate}
          sortable={!includeDuplicates}
          style={{ width: '25%' }}
          filter={!includeDuplicates}
          filterPlaceholder="Search payee"
          showFilterMatchModes={false}
          editor={!includeDuplicates ? cellEditor : undefined}
          onCellEditComplete={!includeDuplicates ? onCellEditComplete : undefined}
        />
        <Column
          field="memo"
          header="Memo"
          body={memoBodyTemplate}
          style={{ width: '25%' }}
          filter={!includeDuplicates}
          filterPlaceholder="Search memo"
          showFilterMatchModes={false}
          editor={!includeDuplicates ? cellEditor : undefined}
          onCellEditComplete={!includeDuplicates ? onCellEditComplete : undefined}
        />
        <Column
          field="amount"
          header="Amount"
          body={amountBodyTemplate}
          sortable={!includeDuplicates}
          style={{ width: '12%' }}
          editor={!includeDuplicates ? cellEditor : undefined}
          onCellEditComplete={!includeDuplicates ? onCellEditComplete : undefined}
        />
        <Column
          body={actionBodyTemplate}
        />
      </DataTable>
    </>
  );
};
